import React, { useEffect, useState } from "react";
import { CloseButton } from "react-bootstrap";
import {  useModal } from "../../Context/ModalContext";
import { BgColor } from "../../data/cardProperties";
import { useAppDispatch, useAppSelector } from "../../hook/redux";

interface BackgroundProps {
  value: string;
  type: string;
}

function BackgroundModal() {
  const { selectedBackground, loadInPageMultimgEditor } = useAppSelector( ( state ) => state.editor );
  const orientation= useAppSelector((state) => state.editor.loadInPageMultimgEditor.orient)

  const [backgrounds, setBackgrounds] = useState(BgColor);

  const { show, toggleShow, type } = useModal();

  const dispatch = useAppDispatch();

  /**
   * Handles the change of background settings by updating the selected background state in the editor.
   *
   * @param {Object} bg - An object containing the background properties.
   * @param {string} bg.value - The value of the background setting.
   * @param {string} bg.type - The type of the background setting.
   * @param {number} index - The index indicating which background to update.
   * @return {void} This function does not return any value.
   */
  function handleBackgroundChange( bg: { value: string; type: string; }, index: number ) {
    const cleanValue = bg.value.replace(process.env.PREFIX_URL || '', '');
    dispatch({
      type: 'editor/updateSelectedBackground',
      payload: { type: bg.type, value: cleanValue, index: index, orientation: orientation }
    });
  }

  /**
   * Generates the appropriate background style based on the provided background properties.
   * @param bg - The background object containing the type and value of the background.
   * @returns A string representing the CSS style for the background.
   */
  function getBackground(bg: BackgroundProps): string {
    const cleanValue = bg.value.replace(process.env.PREFIX_URL || '', '');
    switch (bg.type) {
      case "base":
        return `url(${process.env.PREFIX_URL}${cleanValue})`;
      case "image":
        const saveValue = cleanValue.includes('.gif') ? `url(${process.env.PREFIX_URL}${cleanValue})` : `url(${process.env.PREFIX_URL}/v2017/html2canvas/proximg.php?img=/assets/v2017/layout-carte-bg/${cleanValue}_${orientation}_prevu.gif)`;
        return saveValue;
      case "color":
        return cleanValue;
      case "gradient":
        return `linear-gradient(${cleanValue})`;
      default:
        return "";
    }
  }

  /**
   * Handles the background click event.
   *
   * This method is triggered when a user interacts with the background element.
   * It checks if the event target matches the current target (indicating the background itself was clicked)
   * and then executes a toggle action to hide or close an element.
   *
   * @param {React.MouseEvent} e - The mouse event triggered by the user's interaction.
   */
  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      toggleShow(false, null, type);
    }
  };

  useEffect(() => {
    const initialBackgrounds = {
      value: loadInPageMultimgEditor.preloadCard,
      type: selectedBackground[orientation].type,
    }
    
    const additionalBackgrounds = window.initialDatas?.backgroundCard && window.initialDatas.backgroundCard !== initialBackgrounds.value ? {
      value: window.initialDatas.backgroundCard,
      type: "image"
    } : null;
    
    setBackgrounds([initialBackgrounds, ...(additionalBackgrounds ? [additionalBackgrounds] : []), ...BgColor]);
  }, [loadInPageMultimgEditor.preloadCard]);

  return (
    <div 
      className={`custom-modal-background ${show && type === "background" ? "" : "hidden"}`}
      onClick={handleBackgroundClick}
    >
      <div
        className={`custom-modal ${show && type === "background" ? "" : "hidden"}`}
        aria-labelledby="contained-modal-title-vcenter"
      >
      <div className="custom-modal-content">
        <div className="custom-modal-header">
          <h4 className="custom-modal-title">
            Choix du background
          </h4>
          <CloseButton
            onClick={ () => toggleShow( false, null, type ) }
            aria-label="Close"
          />
        </div>
        <div className="custom-modal-body">
          <div className="custom-modal-flex">
            { backgrounds.map( ( bg, index ) => {
              const background = getBackground( bg );
              return (
                <div
                  key={ bg.value }
                  className={ `${
                    selectedBackground.index === index ? "active" : ""
                  }` }
                  onClick={ () => {
                    handleBackgroundChange( bg, index )
                    toggleShow( false, null, type )
                  } }
                  style={ {
                    background: background,
                    width: loadInPageMultimgEditor.orient === "h" || loadInPageMultimgEditor.orient === "s" ? "106px" : "76px",
                    height: loadInPageMultimgEditor.orient === "h" ? "76px" : "106px",
                    margin: "5px",
                    cursor: "pointer",
                    backgroundSize: "cover",
                    border:
                      selectedBackground.index === index
                      ? "2px solid #000"
                      : "1px solid #ccc"
                  } }
                />
              );
            } ) }
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default BackgroundModal;